/**
 * Pure option builders for the post-run `StyledSelect` pickers (#24385) —
 * the payslip employee picker, the disbursement dialog's payment-source
 * quick-fill list, and the "Paid from" bank-account picker shared by
 * `StatutoryRecordDialog` and the LK final-pay drawer. React-free so the
 * shaping is unit-testable and `post_run_sections.tsx` stays JSX + wiring.
 */
import type { StyledSelectOption } from '@jumpstone/ui-react';
import type { PaymentSourceAccountOption } from '../../data/wagepro_store';
import {
  type BankAccountLike,
  formatPaymentSourceAccountLabel,
  resolvePaymentSourceQuickFillValue,
} from './post_run_payment_source_picker';

/** The two fields the payslip picker reads off a pay-run line item. */
export interface PayslipEmployeeLine {
  employee_id: string;
  employee_name: string;
}

/**
 * A GL bank/cash account as both `BankAccountOption` (`wagepro_store.tsx`)
 * and `RealPaymentSourceAccountRow` (`oauth_pay_run_post_actions.ts`) shape
 * it — structural, so either row type fits without an adapter. Extends the
 * label owner's `BankAccountLike` with the `id` the option is keyed by.
 */
export interface BankAccountOptionSource extends BankAccountLike {
  id: string;
}

/** One option per pay-run line, keyed by employee id, labelled by name. */
export function build_payslip_employee_options(
  line_items: readonly PayslipEmployeeLine[],
): StyledSelectOption[] {
  return line_items.map((line) => ({
    value: line.employee_id,
    label: line.employee_name,
  }));
}

/**
 * One option per account, keyed by the account's own id and labelled by the
 * shared `formatPaymentSourceAccountLabel` (`"<code> · <name>"`, else the
 * bare name) — the single owner of that label, so the statutory dialog and
 * the LK final-pay drawer render exactly what the disbursement quick-fill
 * list and Data Centre's Bank tab do.
 */
export function build_bank_account_options(
  accounts: readonly BankAccountOptionSource[],
): StyledSelectOption[] {
  return accounts.map((account) => ({
    value: account.id,
    label: formatPaymentSourceAccountLabel(account),
  }));
}

/**
 * Options for the disbursement dialog's quick-fill list. The option VALUE is
 * the quick-fill value (`resolvePaymentSourceQuickFillValue` — GL code, else
 * id), because that is what the picker writes into the editable account
 * -number field on selection (see `post_run_payment_source_picker.ts`).
 *
 * Two accounts resolving to the same quick-fill value collapse onto the
 * first: `StyledSelect` keys its rows by `value`, and picking either would
 * fill the identical string anyway (`findPaymentSourceAccountByQuickFillValue`
 * already returns the first match), so the duplicate row carried no extra
 * information — only a duplicate-key render hazard.
 */
export function build_payment_source_quick_fill_options(
  accounts: readonly PaymentSourceAccountOption[],
): StyledSelectOption[] {
  const seen = new Set<string>();
  const options: StyledSelectOption[] = [];
  for (const account of accounts) {
    const value = resolvePaymentSourceQuickFillValue(account);
    if (seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: formatPaymentSourceAccountLabel(account) });
  }
  return options;
}
